import { effect, inject, Injectable, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ListingCardData } from '@features/listing/components/listing-card/listing-card.component';
import { SearchService } from '@features/search/services/search.service';
import { filter } from 'rxjs';

import { CHATBOT_CONTEXT_IDS } from '../constants/chatbot-contexts';
import { ChatbotContextService } from './chatbot-context';

const SEARCH_PAGE_KEY = 'search';
const FIRST_PAGE_SIZE = 20;

@Injectable({
    providedIn: 'root',
})
export class ChatbotSearchResultsContextService {
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly contextService = inject(ChatbotContextService);
    private readonly searchService = inject(SearchService);

    private onSearchPage = false;

    constructor() {
        effect(() => {
            const allItems = this.searchService.searchQuery().allItems();

            untracked(() => {
                if (this.onSearchPage) {
                    this.updateSearchResultsContext(allItems);
                }
            });
        });
    }

    initialize(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const wasOnSearch = this.onSearchPage;

                this.onSearchPage = this.getPageKey() === SEARCH_PAGE_KEY;

                if (this.onSearchPage) {
                    this.updateSearchResultsContext(this.searchService.searchQuery().allItems());
                } else if (wasOnSearch) {
                    this.contextService.remove(CHATBOT_CONTEXT_IDS.SEARCH_RESULTS_LISTINGS);
                }
            });
    }

    private updateSearchResultsContext(allItems: ListingCardData[]): void {
        if (allItems.length === 0) {
            this.contextService.remove(CHATBOT_CONTEXT_IDS.SEARCH_RESULTS_LISTINGS);

            return;
        }

        const firstPage = allItems.slice(0, FIRST_PAGE_SIZE);

        const bulletLines = firstPage.map((listing, index) => {
            const segments: string[] = [];

            if (listing.address) {
                segments.push(listing.address);
            }

            if (listing.price !== null && listing.price !== undefined) {
                segments.push(`$${listing.price.toLocaleString()}`);
            }

            const bedroomsValue = listing.bedrooms;
            const bathroomsValue = listing.bathrooms;

            if (bedroomsValue !== null && bedroomsValue !== undefined &&
                bathroomsValue !== null && bathroomsValue !== undefined) {
                segments.push(`${bedroomsValue}bd/${bathroomsValue}ba`);
            } else if (bedroomsValue !== null && bedroomsValue !== undefined) {
                segments.push(`${bedroomsValue}bd`);
            } else if (bathroomsValue !== null && bathroomsValue !== undefined) {
                segments.push(`${bathroomsValue}ba`);
            }

            if (listing.neighborhood) {
                segments.push(listing.neighborhood);
            }

            return `${index + 1}. ${segments.join(' — ')}`;
        }).join('\n');

        const message =
            `User is viewing the first ${firstPage.length} of ${allItems.length} listings in search results:\n${bulletLines}`;

        const data = firstPage.reduce((accumulator, listing, index) => {
            const prefix = `listing_${index + 1}`;

            accumulator[`${prefix}_id`] = listing.id.toString();

            if (listing.address) {
                accumulator[`${prefix}_address`] = listing.address;
            }

            if (listing.price !== null && listing.price !== undefined) {
                accumulator[`${prefix}_price`] = listing.price.toString();
            }

            if (listing.bedrooms !== null && listing.bedrooms !== undefined) {
                accumulator[`${prefix}_bedrooms`] = listing.bedrooms.toString();
            }

            if (listing.bathrooms !== null && listing.bathrooms !== undefined) {
                accumulator[`${prefix}_bathrooms`] = listing.bathrooms.toString();
            }

            if (listing.squareFootage !== null && listing.squareFootage !== undefined) {
                accumulator[`${prefix}_squareFootage`] = listing.squareFootage.toString();
            }

            if (listing.neighborhood) {
                accumulator[`${prefix}_neighborhood`] = listing.neighborhood;
            }

            return accumulator;
        }, {} as Record<string, string>);

        this.contextService.set(
            CHATBOT_CONTEXT_IDS.SEARCH_RESULTS_LISTINGS,
            'Search Results',
            message,
            data,
        );
    }

    private getPageKey(): string {
        let route: ActivatedRoute = this.activatedRoute;

        while (route.firstChild) {
            route = route.firstChild;
        }

        const key = (route.snapshot.data['key'] as string | null) ?? '';

        return key.replace('prnyc-', '');
    }
}
